export type AppointmentReviewType = 'stylist-review' | 'client-review';

export interface ReviewParty {
  _id?: string;
  id?: string;
  name?: string;
  profilePhoto?: unknown;
}

export interface SlotReview {
  reviewType?: string;
  client?: ReviewParty | string | null;
  stylist?: ReviewParty | string | null;
  review?: { text?: string };
  rating?: { overall?: number };
  createdAt?: string;
}

export interface ViewerIdentity {
  id?: string;
  _id?: string;
  name?: string;
  profilePhoto?: unknown;
}

export interface AppointmentReviewSections {
  ownHeading: 'Your Review' | 'Review Stylist' | 'Review Client';
  showWriteAction: boolean;
  ownReview: SlotReview | null;
  ownAuthorName: string;
  incomingHeading: "Stylist's Review of You" | "Client's Review of You" | null;
  incomingReview: SlotReview | null;
  incomingAuthorName: string;
}

export function acceptReviewForSlot<T extends { reviewType?: string }>(
  review: T | null | undefined,
  expectedType: AppointmentReviewType
): T | null {
  if (!review) return null;
  if (review.reviewType !== expectedType) {
    console.warn(
      `[appointment-reviews] discarded ${review.reviewType || 'unknown'} response for the ${expectedType} slot`
    );
    return null;
  }
  return review;
}

function asParty(party: ReviewParty | string | null | undefined): ReviewParty | null {
  if (!party || typeof party === 'string') return null;
  return party;
}

export function reviewAuthorParty(review: SlotReview | null | undefined): ReviewParty | null {
  if (!review) return null;
  if (review.reviewType === 'client-review') return asParty(review.stylist);
  if (review.reviewType === 'stylist-review') return asParty(review.client);
  return null;
}

export function reviewAuthorName(
  review: SlotReview | null | undefined,
  viewer?: ViewerIdentity | null
): string {
  const party = reviewAuthorParty(review);
  const authorId = party?._id || party?.id || '';
  const viewerId = viewer?.id || viewer?._id || '';
  const viewerIsAuthor = Boolean(authorId && viewerId && String(authorId) === String(viewerId));
  if (viewerIsAuthor) {
    return viewer?.name || party?.name || 'You';
  }
  return party?.name || 'Anonymous';
}

export function buildAppointmentReviewSections(
  role: 'client' | 'stylist',
  stylistReview: SlotReview | null,
  clientReview: SlotReview | null,
  viewer?: ViewerIdentity | null
): AppointmentReviewSections {
  const ownReview = role === 'client' ? stylistReview : clientReview;
  const incomingReview = role === 'client' ? clientReview : stylistReview;

  return {
    ownHeading: ownReview
      ? 'Your Review'
      : role === 'client'
        ? 'Review Stylist'
        : 'Review Client',
    showWriteAction: !ownReview,
    ownReview,
    ownAuthorName: ownReview ? reviewAuthorName(ownReview, viewer) : '',
    incomingHeading: incomingReview
      ? role === 'client'
        ? "Stylist's Review of You"
        : "Client's Review of You"
      : null,
    incomingReview,
    incomingAuthorName: incomingReview ? reviewAuthorName(incomingReview) : '',
  };
}
